import { useState, useEffect } from 'react'
import axios from 'axios'
import {
	getApplicablePriorityDealers,
	matchesPriorityDealer
} from './dealerConfig'

export const dealerTypes = [
	{ name: 'Elite Dealer', type: 'elite' },
	{ name: 'Authorised Dealer', type: 'authoriseddealer' },
	{ name: 'Robotic Repairs', type: 'roboticrepairs' },
	{ name: 'Spare Parts', type: { $in: ['elite', 'roboticrepairs'] } },
	{ name: 'Mineral Swim', type: 'mineralswim' },
	{ name: 'Water Treatment Repairs', type: 'watertreatmentrepairs' },
	{ name: 'Commercial Repairs', type: 'commercial' },
	{ name: 'Pool Builder', type: 'elitebuilder' },
	{ name: 'Focus Chemicals', type: 'focuschemicals'}
]
export function useAddressSearch(
	searchTerm,
	locale,
	types = 'postcode%2Caddress'
) {
	const [addresses, setAddresses] = useState([])
	useEffect(() => {
		if (searchTerm.trim() !== '') {
			let isFresh = true
			fetchAddresses(searchTerm, locale, types)
				.then(addresses => {
					const features = addresses.features.map(
						({ place_name, geometry }) => ({
							name: place_name,
							coordinates: geometry.coordinates,
						})
					)
					if (isFresh) setAddresses(features)
					return
				})
				.catch(e => e)
			return () => (isFresh = false)
		}
	}, [searchTerm, locale, types])
	return addresses
}
const addressCache = {}
const dealerCache = {}
const isBusyDealer = dealer => dealer?.branch?.is_busy === true
const filterBusyDealers = dealers => dealers.filter(dealer => dealer && !isBusyDealer(dealer))
const getFetchLimit = (locale, limit = 2) => {
	const defaultLimit = locale === '' || locale === 'en-NZ' ? 1 : limit
	return Math.max(defaultLimit, 3) * 2
}

const fetchAddresses = async (value, locale, types) => {
	let country
	locale === '' || locale === 'en-AU'
		? (country = 'au')
		: locale === 'en-NZ'
		? (country = 'nz')
		: (country = 'au')
	const ENDPOINT = 'mapbox.places'
	const SEARCH_TEXT = encodeURI(value)
	const MAPBOX_TOKEN = process.env.GATSBY_MAPBOX_TOKEN
	const URI = `https://api.mapbox.com/geocoding/v5/${ENDPOINT}/${SEARCH_TEXT}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&country=${country}&types=${types}&limit=5`
	if (addressCache[value]) {
		return Promise.resolve(addressCache[value])
	}
	try {
		const response = await axios.get(URI)
		if (response.status === 404)
			throw new Error('MapBox found no matches for that address / postcode.')
		return response.data
	} catch (error) {
		// console.log(error)
	}
}
export function useDealerSearch(type, coordinates, setDealer, locale, limit) {
	const [dealers, setDealers] = useState([])
	// console.log('dealers', dealers)
	const [loading, setLoading] = useState(false)
	useEffect(() => {
		if (type && Array.isArray(coordinates) && coordinates.length === 2) {
			let isFresh = true
			const filter = JSON.stringify({ type, status: 'active' })
			setLoading(true)
			fetchDealers(filter, coordinates, 3000, 3, locale, limit)
				.then(async dealersByDistance => {
					if (isFresh) {
						const data = filterBusyDealers(dealersByDistance)
						const hasOne = data.length === 1
						const hasTwo = data.length === 2
						const inCluster =
						typeof type === 'object' ||
							/elite|elitebuilder|mineralswim|roboticrepairs|watertreatmentrepairs|commercial|authoriseddealer/i.test(//elite was added 15/07/2025
								type
							) ||
							(hasTwo && data[1].meta.distance <= 6000) ||
							(hasOne && data[0].meta.distance <= 6000)

						// New scalable priority dealer logic
						const { priorityDealers, otherDealers } = await handlePriorityDealers(data, coordinates, type)

						let cluster
						if (priorityDealers.length > 0) {
							// Priority dealers found - combine with nearest other dealers
							const limitNum = typeof limit === 'number' && limit > 0 ? limit : 3
							const remainingSlots = Math.max(0, limitNum - priorityDealers.length)
							const nearestOthers = otherDealers.slice(0, remainingSlots)
							cluster = [...priorityDealers, ...nearestOthers]
						} else {
							// No priority dealers - use original logic
							cluster = inCluster
								? data && data.length >= 4
									? data.slice(0, limit - 1)
									: data
								: [data[0]]
						}
						const availableDealers = cluster.filter(Boolean)
						setDealers(availableDealers)
						setDealer(availableDealers[0])
						setLoading(false)
					}
					return
				})
				.catch(e => e)
			return () => (isFresh = false)
		}
	}, [coordinates, setDealer, type, locale, limit])
	return { dealers, loading }
}
export async function dealerSearchFunction(type, coordinates, setDealer, locale, limit) {
	let dealers = []
		if (type && Array.isArray(coordinates) && coordinates.length === 2) {
			const filter = JSON.stringify({ type, status: 'active' })
			await fetchDealers(filter, coordinates, 3000, 3, locale, limit)
				.then(async dealersByDistance => {
						const data = filterBusyDealers(dealersByDistance)
						const hasOne = data.length === 1
						const hasTwo = data.length === 2
						const inCluster =
						typeof type === 'object' ||
							/elite|elitebuilder|mineralswim|roboticrepairs|watertreatmentrepairs|commercial|authoriseddealer/i.test(//elite was added 15/07/2025
								type
							) ||
							(hasTwo && data[1].meta.distance <= 6000) ||
							(hasOne && data[0].meta.distance <= 6000)

						// New scalable priority dealer logic
						const { priorityDealers, otherDealers } = await handlePriorityDealers(data, coordinates, type)

						let cluster
						if (priorityDealers.length > 0) {
							// Priority dealers found - combine with nearest other dealers
							const limitNum = typeof limit === 'number' && limit > 0 ? limit : 3
							const remainingSlots = Math.max(0, limitNum - priorityDealers.length)
							const nearestOthers = otherDealers.slice(0, remainingSlots)
							cluster = [...priorityDealers, ...nearestOthers]
						} else {
							// No priority dealers - use original logic
							cluster = inCluster
								? data && data.length >= 4
									? data.slice(0, limit - 1)
									: data
								: [data[0]]
						}

						dealers = cluster.filter(Boolean)
					return
				})

				.catch(e => e)
		}
	return  dealers
}
// Function to find a specific dealer by name pattern
const findDealerByName = async (namePattern) => {
	const url = '/.netlify/functions/graphql'
	const query = `query findDealerByName($namePattern: String!) {
		dealers(query: $namePattern) {
			edges {
				_id
				name
				email
				phone
				address {
					fullAddress
					city
					state
				}
				location {
					coordinates
				}
				logo
				social {
					website
				}
				type
				branch {
					is_busy
				}
			}
		}
	}`

	try {
		const res = await axios.post(url, {
			query,
			variables: { namePattern: JSON.stringify({ name: { $regex: namePattern, $options: 'i' } }) }
		})
		const dealers = res.data.data.dealers.edges
		return dealers.find(dealer =>
			dealer.name && dealer.name.toLowerCase().includes(namePattern.toLowerCase())
		)
	} catch (error) {
		// console.error('Error finding dealer by name:', error)
		return null
	}
}

// New scalable function to handle priority dealers
const handlePriorityDealers = async (data, coordinates, searchType) => {
	// Get applicable priority dealers for this location and search type
	const applicablePriorityDealers = getApplicablePriorityDealers(coordinates, searchType)

	if (applicablePriorityDealers.length === 0) {
		return { priorityDealers: [], otherDealers: data }
	}

	const priorityDealers = []
	const otherDealers = [...data]

	// Process each applicable priority dealer
	for (const priorityDealer of applicablePriorityDealers) {
		// Check if priority dealer is already in the results
		let foundInResults = data.find(dealer =>
			matchesPriorityDealer(dealer.name, priorityDealer)
		)

		// If not found in results, search for it dynamically
		if (!foundInResults) {
			try {
				const foundDealer = await findDealerByName(priorityDealer.searchPattern)
				if (foundDealer && !isBusyDealer(foundDealer)) {
					// Transform the found dealer to match expected format
					foundInResults = {
						_id: foundDealer._id,
						name: foundDealer.name,
						type: foundDealer.type || priorityDealer.dealerTypes,
						address: foundDealer.address,
						phone: foundDealer.phone,
						email: foundDealer.email,
						location: foundDealer.location,
						meta: {
							distance: 0 // Force to top for priority dealers
						},
						logo: foundDealer.logo,
						social: foundDealer.social,
						branch: foundDealer.branch,
						priority: priorityDealer.priority,
						forceTop: priorityDealer.forceTop
					}
				}
			} catch (error) {
				// console.error(`Error searching for ${priorityDealer.name}:`, error)
			}
		} else {
			// Add priority metadata to existing dealer
			foundInResults.priority = priorityDealer.priority
			foundInResults.forceTop = priorityDealer.forceTop
		}

		if (foundInResults && !isBusyDealer(foundInResults)) {
			priorityDealers.push(foundInResults)
			// Remove from other dealers if it was in the original results
			const index = otherDealers.findIndex(dealer =>
				matchesPriorityDealer(dealer.name, priorityDealer)
			)
			if (index !== -1) {
				otherDealers.splice(index, 1)
			}
		}
	}

	// Sort priority dealers by priority (higher number = higher priority)
	priorityDealers.sort((a, b) => (b.priority || 0) - (a.priority || 0))

	return { priorityDealers, otherDealers }
}

const fetchDealers = async (
	filter,
	coordinates,
	delay,
	tries,
	locale,
	limit = 2
) => {
	const fetchLimit = getFetchLimit(locale, limit)
	const url = '/.netlify/functions/graphql'
	const variables = {
		query: filter,
		coordinates,
		limit: fetchLimit,
		maxDistance: 1400000,
	}
	// console.log(variables)
	const query = `query dealersByDistance($query:String,$coordinates:[Float!],$maxDistance:Int,$limit:Int) {dealersByDistance(query:$query,coordinates:$coordinates,maxDistance:$maxDistance,limit:$limit) {_id,affiliateId,name,email,phone,social{minisite},address{fullAddress,city,state,postalCode},logo,location{coordinates},branch{is_busy},meta{distance},type,salesForceId}}`
	if (dealerCache[filter && coordinates]) {
		return Promise.resolve(dealerCache[filter && coordinates])
	}
	const onError = async error => {
		let triesLeft = tries - 1
		if (!triesLeft) throw error
		return wait(delay).then(() =>
			fetchDealers(filter, coordinates, delay, triesLeft)
		)
	}
	try {
		const res = await axios.post(url, { query, variables })
		// console.log('Result: ', res)
		// console.log('Request: ', { query, variables })
		const result = res.data.data.dealersByDistance.filter(d => d !== null)
		return result
	} catch (error) {
		await onError(error)
	}
}
const wait = delay => new Promise(resolve => setTimeout(resolve, delay))
